"use client";

import Link from "next/link";
import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";

import { TurnstileCheck, TurnstileScript, useTurnstile } from "@/components/ownly/TurnstileWidget";
import { useDisplayTimeZone } from "@/components/ownly/useDisplayTimeZone";
import {
  failureMessage,
  formatLongDay,
  formatTime,
  HOST_TIME_ZONE,
  minutesInWords,
  regroupDays,
  sameClock,
  splitByPartOfDay,
  zoneAbbreviation,
  zoneLabel,
  type Availability,
  type BookingFailure,
} from "@/lib/own-booking";

export { useDisplayTimeZone, useVisitorTimeZone } from "@/components/ownly/useDisplayTimeZone";

/**
 * The date and time picker for Ownly's own booking page, and for moving a booking.
 *
 * TIME ZONES. The diary is London's. A visitor whose clock differs sees their own times by default
 * with a one-press switch to UK time, every time label is in the zone they chose, and the confirm
 * line always says the UK time as well, so nobody turns up an hour out. Slots are regrouped by the
 * visitor's own date (see groupSlotsByDay), so 6:30pm London never sits under the wrong day in
 * Sydney.
 *
 * KEYBOARD. Days and times are plain buttons in order, so Tab and Enter do everything; the chosen
 * one carries aria-pressed. Choosing a time moves focus to the confirm button, which states the
 * full time in words for a screen reader.
 */

type Result = { ok: true } | { ok: false; reason: BookingFailure };

type Status =
  | { kind: "loading" }
  | { kind: "ready"; availability: Availability }
  | { kind: "unavailable"; reason: BookingFailure; hostName?: string | null };

/**
 * A person's own page (/book/wendy) before they can take bookings: a new member of staff whose
 * Google Calendar is not connected yet, or someone switched off for now. Lead Engine makes the page
 * the moment they are added, so a link may well be out there already; it must read as "not yet",
 * never as a broken page, and always offer the shared /book instead.
 */
export function HostNotTakingBookings({ name }: { name?: string | null }) {
  return (
    <div role="status" className="ownly-step-in-fwd">
      <h3 className="ownly-display ownly-card-title text-balance text-white">
        {name ? `${name} isn't taking bookings online just yet` : "This person isn't taking bookings online just yet"}
      </h3>
      <p className="mt-3 max-w-[52ch] text-copy leading-[23px] text-white/70">
        You can still book a call with the Ownly team. Pick a time that suits you and the right person will take it.
      </p>
      <div className="mt-6">
        <Link
          href="/book"
          className="ownly-btn-primary inline-flex h-12 w-full items-center justify-center rounded-full bg-ownly-accent px-6 text-copy font-semibold text-white shadow-[var(--ownly-cta-shadow)] transition-[box-shadow,transform,background-color] duration-300 ease-out hover:-translate-y-0.5 hover:bg-[#3d8aff] hover:shadow-[var(--ownly-cta-shadow-hover)] active:translate-y-0 active:scale-[0.98] sm:w-auto sm:min-w-[210px]"
        >
          Book with the team
        </Link>
      </div>
    </div>
  );
}

/** "Thirty minutes on Google Meet", "Fifteen minutes on Google Meet or by phone". */
function lengthLine(a: Availability | null): string {
  const minutes = a?.meetingType?.minutes ?? a?.slotMinutes ?? 30;
  const types = a?.meetingType?.callTypes ?? ["meet"];
  const how = types.includes("meet") && types.includes("phone") ? "on Google Meet or by phone" : types.includes("phone") ? "by phone" : "on Google Meet";
  return `${minutesInWords(minutes)} ${how}`;
}

export function SlotPicker({
  token,
  host,
  type,
  extras,
  onLoaded,
  confirmLabel,
  busyLabel,
  recipientNote,
  needsSecurityCheck,
  contactEmail,
  fallback,
  onConfirm,
  onTimeChosen,
  hostName = null,
}: {
  /** Moving a booking: its manage token, so its own time does not block the slots beside it. */
  token?: string;
  /** A person's own page (/book/tom). Absent = the shared /book. */
  host?: string | null;
  /** Meeting type slug. Absent = the 30-minute review. */
  type?: string | null;
  /** Shown above the confirm button once times have loaded: call type, phone, reminders. */
  extras?: (availability: Availability) => React.ReactNode;
  /** Told what loaded, so the page around the picker can match it. */
  onLoaded?: (availability: Availability) => void;
  confirmLabel: string;
  busyLabel: string;
  /** e.g. "We will send the invite to jo@example.com." */
  recipientNote?: string;
  needsSecurityCheck: boolean;
  contactEmail: string;
  /** What to show when booking is not working: the backup calendar link and the email. */
  fallback: React.ReactNode;
  /** `zone` is the zone the visitor was reading the times in, for their confirmation email. */
  onConfirm: (slot: string, turnstileToken: string | null, zone: string) => Promise<Result>;
  onTimeChosen?: () => void;
  /** On a person's own page: their name, for the "not taking bookings yet" message. */
  hostName?: string | null;
}) {
  const headingId = useId();
  const dayLabelId = useId();
  const [status, setStatus] = useState<Status>({ kind: "loading" });
  const { zone, browserZone: viewerZone, setZone } = useDisplayTimeZone();
  const [dayKey, setDayKey] = useState<string | null>(null);
  const [slot, setSlot] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const confirmRef = useRef<HTMLButtonElement>(null);
  const turnstile = useTurnstile();

  const load = useCallback(async () => {
    setStatus((s) => (s.kind === "ready" ? s : { kind: "loading" }));
    try {
      const res = await fetch("/api/booking/availability", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(token ? { token } : { ...(host ? { host } : {}), ...(type ? { type } : {}) }),
        cache: "no-store",
      });
      const json = (await res.json()) as ({ ok: true } & Availability) | { ok: false; reason: BookingFailure; hostName?: string | null };
      if (json.ok) {
        setStatus({ kind: "ready", availability: json });
        onLoaded?.(json);
      }
      else setStatus({ kind: "unavailable", reason: json.reason, hostName: typeof json.hostName === "string" ? json.hostName : null });
    } catch {
      setStatus({ kind: "unavailable", reason: "network" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onLoaded is a notification, not an input
  }, [token, host, type]);

  useEffect(() => {
    void load();
  }, [load]);

  const showZoneSwitch = viewerZone !== HOST_TIME_ZONE && !sameClock(viewerZone, HOST_TIME_ZONE);

  const days = useMemo(
    () => (status.kind === "ready" ? regroupDays(status.availability.days, zone) : []),
    [status, zone],
  );
  const day = days.find((d) => d.key === dayKey) ?? days[0] ?? null;

  /* A chosen time that vanished on refresh (taken meanwhile) is let go. */
  useEffect(() => {
    if (slot && !days.some((d) => d.slots.includes(slot))) setSlot(null);
  }, [days, slot]);

  async function confirm() {
    if (!slot || submitting) return;
    if (needsSecurityCheck && !turnstile.ready) {
      setNotice("The security check is still running. Give it a second and try again.");
      return;
    }
    setSubmitting(true);
    setNotice(null);
    try {
      const result = await onConfirm(slot, turnstile.token, zone);
      if (!result.ok) {
        setNotice(failureMessage(result.reason, contactEmail));
        if (result.reason === "slot_taken" || result.reason === "slot_unavailable") {
          setSlot(null);
          await load();
        }
        if (result.reason === "unavailable" || result.reason === "network") {
          setStatus({ kind: "unavailable", reason: result.reason });
        }
      }
    } finally {
      setSubmitting(false);
      if (needsSecurityCheck) turnstile.reset();
    }
  }

  if (status.kind === "unavailable" && status.reason === "host_not_ready") {
    return <HostNotTakingBookings name={status.hostName ?? hostName} />;
  }

  if (status.kind === "unavailable") {
    return (
      <div role="status" className="ownly-step-in-fwd">
        <h3 className="ownly-display ownly-card-title text-white">Booking online is not working right now</h3>
        <p className="mt-3 max-w-[52ch] text-copy leading-[23px] text-white/70">{failureMessage(status.reason, contactEmail)}</p>
        <div className="mt-6">{fallback}</div>
      </div>
    );
  }

  const ukLine =
    slot && zone !== HOST_TIME_ZONE && !sameClock(zone, HOST_TIME_ZONE)
      ? `That is ${formatTime(slot, HOST_TIME_ZONE)} UK time.`
      : null;

  return (
    <div>
      {needsSecurityCheck ? <TurnstileScript /> : null}
      <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-4">
        <div>
          <h3 id={headingId} className="ownly-display ownly-card-title text-white">
            Pick a time
          </h3>
          <p className="mt-2 text-copy leading-[21px] text-white/60">
            {lengthLine(status.kind === "ready" ? status.availability : null)}. Times shown in {zoneLabel(zone)}.
          </p>
        </div>
        {showZoneSwitch ? (
          <div role="group" aria-label="Show times in" className="inline-flex rounded-full border border-white/[0.14] bg-white/[0.03] p-1">
            {[
              { value: viewerZone, label: zoneLabel(viewerZone) },
              { value: HOST_TIME_ZONE, label: "UK time" },
            ].map((option) => (
              <button
                key={option.value}
                type="button"
                aria-pressed={zone === option.value}
                onClick={() => setZone(option.value)}
                className={`min-h-[36px] rounded-full px-3.5 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ownly-accent ${
                  zone === option.value ? "bg-white/[0.12] text-white" : "text-white/60 hover:text-white"
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        ) : null}
      </div>

      {status.kind === "ready" && status.availability.preview ? (
        <p className="mt-5 rounded-xl border border-ownly-accent/30 bg-ownly-accent/[0.08] px-4 py-3 text-copy leading-[21px] text-white/80">
          Preview: these are the working hours, not the real diary. Nothing you book here is real.
        </p>
      ) : status.kind === "ready" && status.availability.dryRun ? (
        <p className="mt-5 rounded-xl border border-ownly-accent/30 bg-ownly-accent/[0.08] px-4 py-3 text-copy leading-[21px] text-white/80">
          Preview: these are the real free times, but nothing you book here goes in the diary.
        </p>
      ) : null}

      {status.kind === "loading" ? (
        <div aria-busy="true" aria-label="Loading the free times" className="mt-7">
          <div className="grid grid-cols-4 gap-2 sm:grid-cols-6">
            {Array.from({ length: 6 }, (_, i) => (
              <div key={i} className="h-[76px] rounded-xl bg-white/[0.05] motion-safe:animate-pulse" />
            ))}
          </div>
          <div className="mt-7 grid grid-cols-3 gap-2 sm:grid-cols-4">
            {Array.from({ length: 8 }, (_, i) => (
              <div key={i} className="h-12 rounded-xl bg-white/[0.05] motion-safe:animate-pulse" />
            ))}
          </div>
          <p className="sr-only" role="status">
            Loading the free times.
          </p>
        </div>
      ) : days.length === 0 ? (
        <div role="status" className="mt-7 border-t border-white/[0.1] pt-6">
          <p className="text-body font-semibold text-white">Nothing free in the next week.</p>
          <p className="mt-2 max-w-[52ch] text-copy leading-[23px] text-white/70">
            The diary is full for now. Email{" "}
            <a href={`mailto:${contactEmail}`} className="font-medium text-ownly-accent-text underline decoration-ownly-accent/35 underline-offset-2">
              {contactEmail}
            </a>{" "}
            with a couple of times that suit you and we will fit you in.
          </p>
        </div>
      ) : (
        <>
          <p id={dayLabelId} className="sr-only">
            Choose a day
          </p>
          <div role="group" aria-labelledby={dayLabelId} className="mt-7 grid grid-cols-4 gap-2 sm:grid-cols-7">
            {days.map((d) => {
              const chosen = d.key === day?.key;
              return (
                <button
                  key={d.key}
                  type="button"
                  aria-pressed={chosen}
                  aria-label={`${formatLongDay(d.slots[0], zone)}, ${d.slots.length} ${d.slots.length === 1 ? "time" : "times"} free`}
                  onClick={() => {
                    setDayKey(d.key);
                    setSlot(null);
                    setNotice(null);
                  }}
                  className={`flex min-h-[76px] flex-col items-center justify-center rounded-xl border px-1 py-2 transition-[border-color,background-color,box-shadow] duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ownly-accent ${
                    chosen
                      ? "border-ownly-accent/60 bg-ownly-accent/15 text-white shadow-[0_0_0_1px_rgba(43,127,255,0.25),0_10px_28px_-18px_rgba(43,127,255,0.55)]"
                      : "border-white/[0.14] bg-white/[0.02] text-white/85 hover:border-white/[0.32] hover:bg-white/[0.05]"
                  }`}
                >
                  <span className="text-sm leading-none text-white/60">{d.weekday}</span>
                  <span className="mt-1.5 text-[22px] font-semibold leading-none tracking-[-0.02em]">{d.dayOfMonth}</span>
                  <span className="mt-1 text-sm leading-none text-white/55">{d.month}</span>
                </button>
              );
            })}
          </div>

          {day ? (
            <div key={`${day.key}-${zone}`} className="ownly-step-in-fwd mt-7">
              <p className="text-copy font-medium text-white/80">{formatLongDay(day.slots[0], zone)}</p>
              {splitByPartOfDay(day.slots, zone).map((part) => (
                <div key={part.label} role="group" aria-label={`${part.label} times`} className="mt-4">
                  <p className="text-sm leading-[20px] text-white/50">{part.label}</p>
                  <div className="mt-2 grid grid-cols-3 gap-2 sm:grid-cols-4">
                    {part.slots.map((s) => {
                      const chosen = s === slot;
                      return (
                        <button
                          key={s}
                          type="button"
                          aria-pressed={chosen}
                          onClick={() => {
                            setSlot(s);
                            setNotice(null);
                            onTimeChosen?.();
                            requestAnimationFrame(() => confirmRef.current?.focus({ preventScroll: true }));
                          }}
                          className={`h-12 rounded-xl border text-[16px] tabular-nums transition-[border-color,background-color,box-shadow] duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ownly-accent ${
                            chosen
                              ? "border-ownly-accent bg-ownly-accent/20 font-semibold text-white"
                              : "border-white/[0.14] bg-white/[0.02] text-white/85 hover:border-white/[0.32] hover:bg-white/[0.05]"
                          }`}
                        >
                          {formatTime(s, zone)}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          ) : null}
        </>
      )}

      {notice ? (
        <p role="alert" className="mt-6 rounded-xl border border-[#E05A45]/35 bg-[#E05A45]/[0.1] px-4 py-3 text-copy text-[#F2AA9D]">
          {notice}
        </p>
      ) : null}

      {status.kind === "ready" && days.length > 0 ? (
        <div className="mt-7 border-t border-white/[0.1] pt-6">
          {slot ? (
            <div aria-live="polite">
              <p className="text-body font-semibold leading-[25px] text-white">
                {formatLongDay(slot, zone)}, {formatTime(slot, zone)} to{" "}
                {formatTime(new Date(new Date(slot).getTime() + status.availability.slotMinutes * 60_000).toISOString(), zone)}
                <span className="font-normal text-white/55"> ({zoneAbbreviation(slot, zone)})</span>
              </p>
              {ukLine ? <p className="mt-1 text-copy text-white/65">{ukLine}</p> : null}
              {recipientNote ? <p className="mt-1 text-copy leading-[21px] text-white/60">{recipientNote}</p> : null}
            </div>
          ) : (
            <p className="text-copy text-white/60">Choose a time above.</p>
          )}
          {extras && status.kind === "ready" ? <div className="mt-5">{extras(status.availability)}</div> : null}
          {needsSecurityCheck ? (
            <div className="mt-5">
              <TurnstileCheck turnstile={turnstile} />
            </div>
          ) : null}
          <div className="mt-5">
            <button
              ref={confirmRef}
              type="button"
              onClick={() => void confirm()}
              disabled={!slot || submitting}
              aria-describedby={headingId}
              className="ownly-btn-primary inline-flex h-12 w-full items-center justify-center rounded-full bg-ownly-accent px-6 text-copy font-semibold text-white shadow-[var(--ownly-cta-shadow)] transition-[box-shadow,transform,background-color] duration-300 ease-out hover:-translate-y-0.5 hover:bg-[#3d8aff] hover:shadow-[var(--ownly-cta-shadow-hover)] active:translate-y-0 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-45 sm:w-auto sm:min-w-[210px]"
            >
              {submitting ? busyLabel : confirmLabel}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
